/**
 * DealTimeline.tsx — the deal page's Timeline card (deal detail slice 2,
 * §39.9 order 2; spec §3.4 C): All · Activity · Changes over ONE merged list
 * (dealTimeline.ts), and the composer — Note inline with Save, writing a note
 * activity through the existing POST /v1/sales/activities; Call, Email and
 * Meeting open the ONE Log activity sheet (LogActivityHost) pre-filled with
 * this deal and that type.
 *
 * R8: this composer does NOT replace the Notes card yet. `deal.notes` stays
 * visible and editable in its own card until slice 3 moves it into Details.
 */
import { useRef, useState } from 'react'
import { Phone, Mail, Users, FileText, Plus, Layers, Pencil, CheckSquare } from 'lucide-react'
import { toast } from 'sonner'
import type { SalesActivity as WireActivity } from '../../../../api/types'
import type { DealDetail } from '../../types'
import { createDealActivity, fetchDealCard, fetchContact, relAgo } from '../../api'
import { formatDay } from './dealRail'
import { apiErrorMessage } from '../../../../api/client'
import { refreshSalesFigures } from '../../salesFigures'
import { useLogActivity } from './logActivityShared'
import { useWriteAccess } from '../../../../lib/writeAccess'
import { filterTimeline, type TimelineFilter, type TimelineIcon, type TimelineItem } from './dealTimeline'

const ICON: Record<TimelineIcon, React.ReactNode> = {
  call: <Phone size={13} />, email: <Mail size={13} />, meeting: <Users size={13} />, note: <FileText size={13} />,
  task: <CheckSquare size={13} />, stage: <Layers size={13} />, field: <Pencil size={13} />, created: <Plus size={13} />,
}

const FILTERS: { value: TimelineFilter; label: string }[] = [
  { value: 'all', label: 'All' }, { value: 'activity', label: 'Activity' }, { value: 'change', label: 'Changes' },
]

/** The sheet's types the composer hands off (spec §3.4 C). */
const HANDOFF: { type: 'call' | 'email' | 'meeting'; label: string }[] = [
  { type: 'call', label: 'Call' }, { type: 'email', label: 'Email' }, { type: 'meeting', label: 'Meeting' },
]

export function DealTimeline({ deal, items, loading, changesFailed, activitiesFailed, onNoteCreated }: {
  deal: DealDetail
  items: readonly TimelineItem[]
  /** The reads have not landed yet — skeleton rows, never a spinner (§3.6). */
  loading: boolean
  /** A failed read is NOT an empty list; the card says which half it lacks. */
  changesFailed: boolean
  activitiesFailed: boolean
  onNoteCreated: (created: WireActivity) => void
}) {
  const [filter, setFilter] = useState<TimelineFilter>('all')
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)
  const [opening, setOpening] = useState(false)
  const logActivity = useLogActivity()
  const noteInput = useRef<HTMLInputElement>(null)
  // The composer (a note is an activity create) is a records-level write
  // (lib/writeAccess): without access the timeline reads only.
  const { records } = useWriteAccess()

  async function saveNote() {
    const subject = text.trim()
    if (!subject || saving) return
    setSaving(true)
    try {
      const created = await createDealActivity({
        dealId: deal.id, activityType: 'note', subject,
        ...(deal.contactId ? { contactId: deal.contactId } : {}),
      })
      onNoteCreated(created)
      void refreshSalesFigures()
      setText('')
      toast.success('Note added to the timeline')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to add the note'))
    } finally {
      setSaving(false)
    }
  }

  // The hand-off: the deal mapped exactly as the board maps it, and its
  // primary contact, so the sheet opens the way the board's card opens it.
  async function openSheet(type: 'call' | 'email' | 'meeting' | null, back: HTMLElement | null) {
    if (!logActivity || opening) return
    setOpening(true)
    try {
      const { deal: card, contactId } = await fetchDealCard(deal.id)
      const contact = contactId ? await fetchContact(contactId) : null
      logActivity.open({ deal: card, contact, ...(type ? { type } : {}) }, () => back)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to open the activity form'))
    } finally {
      setOpening(false)
    }
  }

  const shown = filterTimeline(items, filter)

  return (
    <div className="k-card" data-deal-timeline>
      <div className="k-card-head">
        <div>
          <div className="k-card-title">Timeline</div>
          {activitiesFailed && <div className="k-card-sub">Logged activity could not be loaded — showing changes only</div>}
          {changesFailed && <div className="k-card-sub">Changes could not be loaded — showing logged activity only</div>}
        </div>
        <div className="k-seg" role="group" aria-label="Show in the timeline">
          {FILTERS.map(f => (
            <button key={f.value} type="button" aria-pressed={filter === f.value} onClick={() => setFilter(f.value)}>{f.label}</button>
          ))}
        </div>
      </div>
      <div className="p-5 flex flex-col gap-4">
        {/* Composer (spec §3.4 C): Note is the inline entry; the other three
            hand off to the Log activity sheet. Without a host there is no
            sheet, so the hand-off buttons are not offered at all. */}
        {records && <div className="flex flex-col gap-2">
          <div className="k-seg self-start" role="group" aria-label="Entry type">
            {/* Note is the inline entry: its button puts the caret in the box. */}
            <button type="button" aria-pressed="true" onClick={() => noteInput.current?.focus()}>Note</button>
            {logActivity && HANDOFF.map(h => (
              <button key={h.type} type="button" aria-pressed="false" disabled={opening}
                onClick={e => { void openSheet(h.type, e.currentTarget) }}>{h.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input ref={noteInput} value={text} onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') void saveNote() }}
              aria-label="Note about this deal" placeholder="Write a note about this deal…"
              className="h-9 px-3 flex-1 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-muted focus:outline-none focus:border-accent" />
            <button type="button" onClick={() => { void saveNote() }} disabled={!text.trim() || saving} className="k-btn disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>}

        {loading && (
          <div className="flex flex-col gap-4" aria-label="Loading the timeline">
            {[0, 1, 2].map(i => <div key={i} className="k-skeleton" style={{ height: 40 }} />)}
          </div>
        )}

        {!loading && shown.length === 0 && filter === 'activity' && (
          <div className="text-13 text-ink-muted">
            No calls, emails or meetings yet.{' '}
            {logActivity && (
              <button type="button" className="text-accent hover:underline" disabled={opening}
                onClick={e => { void openSheet(null, e.currentTarget) }}>Log the first one ›</button>
            )}
          </div>
        )}
        {!loading && shown.length === 0 && filter === 'change' && (
          <div className="text-13 text-ink-muted">No changes recorded on this deal yet.</div>
        )}

        {!loading && shown.length > 0 && (
          <ol className="flex flex-col gap-4" aria-label="Timeline entries">
            {shown.map(it => (
              <li key={it.key} className="flex items-start gap-3" data-timeline-kind={it.kind}>
                <div className="w-8 h-8 rounded-full bg-surface-panel border border-edge-row flex items-center justify-center text-ink-muted flex-shrink-0" aria-hidden="true">
                  {ICON[it.icon]}
                </div>
                <div className="min-w-0">
                  <div className="text-13 font-semibold text-ink-primary">{it.title}</div>
                  {it.detail && <div className="text-12 text-ink-secondary mt-1">{it.detail}</div>}
                  <div className="text-11 text-ink-muted mt-1">
                    {[it.who, formatDay(it.at), relAgo(it.at)].filter(Boolean).join(' · ')}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  )
}
